import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  ShoppingBag,
  Menu,
  X,
  Sparkles,
  ShieldCheck,
  Truck,
  Instagram,
  MessageCircle,
  Lock,
} from 'lucide-react';
import { MilaLogo } from '@/components/MilaLogo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { STORE_WHATSAPP_NUMBER } from '@/lib/constants';

export default function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();

  // STRICT REQUIREMENT: Storefront is strictly LIGHT MODE.
  // When in public routes, ensure documentElement does NOT have the 'dark' class.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    root.classList.add('light');
    root.style.colorScheme = 'light';
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Read cart count from localStorage (mila_cart)
  useEffect(() => {
    const updateCartCount = () => {
      try {
        const saved = localStorage.getItem('mila_cart');
        if (saved) {
          const items = JSON.parse(saved);
          const count = Array.isArray(items)
            ? items.reduce((acc: number, item: any) => acc + (item.quantity || 1), 0)
            : 0;
          setCartCount(count);
        } else {
          setCartCount(0);
        }
      } catch {
        setCartCount(0);
      }
    };

    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    // Custom event support if pages dispatch cart-updated
    window.addEventListener('cart-updated', updateCartCount);

    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cart-updated', updateCartCount);
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#24211D] flex flex-col font-sans selection:bg-amber-200 selection:text-amber-950">
      {/* ── Top Announcement Bar (Shopcn E-commerce Style) ── */}
      <div className="bg-[#1E1B18] text-[#FAF9F6] text-xs py-2 px-4 transition-colors">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <p className="flex items-center gap-2 font-medium tracking-wide mx-auto sm:mx-0">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Taller de fabricación 3D en Buenos Aires • 3 cuotas sin interés en toda la tienda</span>
          </p>
          <div className="hidden sm:flex items-center gap-4 text-[11px] text-[#D8D4CE]">
            <span>Envíos a todo el país</span>
            <span className="text-white/30">•</span>
            <a
              href={`https://wa.me/${STORE_WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors flex items-center gap-1"
            >
              <MessageCircle className="w-3 h-3 text-emerald-400" />
              <span>WhatsApp Taller</span>
            </a>
          </div>
        </div>
      </div>

      {/* ── Main Storefront Header (Shopcn Warm Editorial Style) ── */}
      <header className="sticky top-0 z-40 w-full border-b border-[#E8E4DC] bg-[#FAF9F6]/90 backdrop-blur-md transition-all">
        <div className="max-w-7xl mx-auto flex h-20 items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Brand Logo (Logo only, no text word) */}
          <Link to="/" className="flex items-center group py-1" aria-label="Mila">
            <MilaLogo
              tight
              className="h-9 sm:h-11 w-auto transition-transform group-hover:scale-105"
              color="#C2410C"
            />
          </Link>

          {/* Desktop Navigation Links (Pill Style) */}
          <nav className="hidden md:flex items-center gap-1 bg-[#F0ECE1]/70 border border-[#E4DFD5] px-3 py-1.5 rounded-full shadow-2xs">
            <Link
              to="/"
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${
                location.pathname === '/'
                  ? 'bg-[#FAF9F6] text-[#C2410C] shadow-2xs'
                  : 'text-[#5C564E] hover:text-[#1E1B18]'
              }`}
            >
              Inicio
            </Link>
            <Link
              to="/tienda"
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${
                location.pathname.startsWith('/tienda')
                  ? 'bg-[#FAF9F6] text-[#C2410C] shadow-2xs'
                  : 'text-[#5C564E] hover:text-[#1E1B18]'
              }`}
            >
              Catálogo & Tienda
            </Link>
            <a
              href="#taller-info"
              className="px-4 py-1.5 text-xs font-semibold rounded-full text-[#5C564E] hover:text-[#1E1B18] transition-all"
            >
              Filosofía 3D
            </a>
          </nav>

          {/* Desktop Right Actions: Cart & Admin Access */}
          <div className="flex items-center gap-3">
            {/* Cart Link with Badge */}
            <Link
              to="/checkout"
              className="relative inline-flex items-center justify-center h-10 px-3.5 rounded-full border border-[#E4DFD5] bg-[#FAF9F6] text-[#1E1B18] hover:bg-[#F0ECE1] transition-colors shadow-2xs"
              title="Ver carrito de compras"
            >
              <ShoppingBag className="h-4 w-4 text-[#C2410C]" />
              <span className="ml-2 text-xs font-semibold hidden sm:inline">Carrito</span>
              {cartCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.2 text-[11px] font-bold bg-[#C2410C] text-white rounded-full min-w-[18px] text-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Admin Login Button */}
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="hidden sm:inline-flex text-xs font-medium text-[#7C756B] hover:text-[#1E1B18] hover:bg-[#F0ECE1] rounded-full px-3.5"
            >
              <Link to="/login" className="flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" />
                <span>Panel Taller</span>
              </Link>
            </Button>

            {/* Mobile Menu Trigger */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden h-10 w-10 text-[#1E1B18] hover:bg-[#F0ECE1] rounded-lg"
              aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* ── Mobile Drawer / Dropdown Menu ── */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E8E4DC] bg-[#FAF9F6] px-4 py-6 space-y-4 shadow-lg animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  location.pathname === '/'
                    ? 'bg-[#F0ECE1] text-[#C2410C]'
                    : 'text-[#1E1B18] hover:bg-[#F0ECE1]/60'
                }`}
              >
                Inicio
              </Link>
              <Link
                to="/tienda"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  location.pathname.startsWith('/tienda')
                    ? 'bg-[#F0ECE1] text-[#C2410C]'
                    : 'text-[#1E1B18] hover:bg-[#F0ECE1]/60'
                }`}
              >
                Catálogo de Piezas
              </Link>
              <Link
                to="/checkout"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold text-[#1E1B18] hover:bg-[#F0ECE1]/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4 text-[#C2410C]" />
                  <span>Mi Carrito</span>
                </div>
                {cartCount > 0 && (
                  <Badge variant="secondary" className="bg-[#C2410C] text-white">
                    {cartCount} {cartCount === 1 ? 'ítem' : 'ítems'}
                  </Badge>
                )}
              </Link>
            </div>

            <Separator className="bg-[#E8E4DC]" />

            <div className="pt-2 flex items-center justify-between px-2">
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="text-xs font-semibold text-[#7C756B] hover:text-[#1E1B18] flex items-center gap-1.5"
              >
                <Lock className="h-3.5 w-3.5" />
                <span>Acceso para administradores</span>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ── Main Store Content ── */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* ── Rich Editorial Footer (Shopcn E-commerce Style) ── */}
      <footer id="taller-info" className="border-t border-[#E8E4DC] bg-[#F4F1EA] text-[#24211D] mt-20 transition-colors">
        {/* Value Proposition Highlights */}
        <div className="border-b border-[#E8E4DC]/80 py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3.5 p-2">
              <div className="h-10 w-10 rounded-xl bg-amber-100/80 text-[#C2410C] flex items-center justify-center shrink-0">
                <Truck className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-[#1E1B18]">Envíos Cuidados a Todo el País</h4>
                <p className="text-xs text-[#7C756B] mt-0.5">Embalaje reforzado y biodegradable para cada pieza.</p>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start gap-3.5 p-2">
              <div className="h-10 w-10 rounded-xl bg-amber-100/80 text-[#C2410C] flex items-center justify-center shrink-0">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-[#1E1B18]">Materiales Nobles & Bio-PLA</h4>
                <p className="text-xs text-[#7C756B] mt-0.5">Polímeros de origen vegetal y acabados satinados.</p>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start gap-3.5 p-2">
              <div className="h-10 w-10 rounded-xl bg-amber-100/80 text-[#C2410C] flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-[#1E1B18]">Garantía de Calidad Mila</h4>
                <p className="text-xs text-[#7C756B] mt-0.5">Revisión minuciosa de cada capa y textura impresa.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Multi-column editorial links */}
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand story */}
            <div className="md:col-span-1 space-y-3">
              <div className="flex items-center">
                <MilaLogo tight className="h-8 w-auto" color="#C2410C" />
              </div>
              <p className="text-xs text-[#6B655B] leading-relaxed">
                Estudio de diseño y fabricación aditiva con estética Japandi. Fusionamos tecnología paramétrica con la calidez del hogar.
              </p>
              <div className="flex items-center gap-3 pt-2">
                <a
                  href="https://instagram.com/miladesign"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-8 w-8 rounded-full border border-[#DCD6CA] flex items-center justify-center text-[#5C564E] hover:text-[#1E1B18] hover:border-[#1E1B18] transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href={`https://wa.me/${STORE_WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-8 w-8 rounded-full border border-[#DCD6CA] flex items-center justify-center text-[#5C564E] hover:text-[#1E1B18] hover:border-[#1E1B18] transition-colors"
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Colecciones */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#1E1B18] mb-3">
                Colección
              </h3>
              <ul className="space-y-2 text-xs text-[#6B655B]">
                <li>
                  <Link to="/tienda" className="hover:text-[#1E1B18] transition-colors">
                    Jarrones & Floreros
                  </Link>
                </li>
                <li>
                  <Link to="/tienda" className="hover:text-[#1E1B18] transition-colors">
                    Macetas Paramétricas
                  </Link>
                </li>
                <li>
                  <Link to="/tienda" className="hover:text-[#1E1B18] transition-colors">
                    Iluminación & Lámparas
                  </Link>
                </li>
                <li>
                  <Link to="/tienda" className="hover:text-[#1E1B18] transition-colors">
                    Accesorios de Escritorio
                  </Link>
                </li>
              </ul>
            </div>

            {/* Taller & Servicios */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#1E1B18] mb-3">
                Taller 3D
              </h3>
              <ul className="space-y-2 text-xs text-[#6B655B]">
                <li>
                  <a href={`https://wa.me/${STORE_WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#1E1B18] transition-colors">
                    Pedidos Especiales & B2B
                  </a>
                </li>
                <li>
                  <span className="text-[#8C8477]">Filamentos PLA Reciclables</span>
                </li>
                <li>
                  <span className="text-[#8C8477]">Prototipado Rápido</span>
                </li>
                <li>
                  <Link to="/login" className="hover:text-[#1E1B18] transition-colors">
                    Panel de Operación Taller
                  </Link>
                </li>
              </ul>
            </div>

            {/* Atención & Medios de Pago */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#1E1B18] mb-3">
                Atención & Pagos
              </h3>
              <p className="text-xs text-[#6B655B] leading-relaxed mb-3">
                Aceptamos Mercado Pago, transferencias con 10% OFF y todas las tarjetas.
              </p>
              <div className="space-y-1 text-xs text-[#6B655B]">
                <p>📍 Taller en Buenos Aires, Argentina</p>
                <p>✉️ hola@miladesign.com</p>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-[#E8E4DC] flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-[#7C756B]">
            <p>© {new Date().getFullYear()} Mila Design Studio. Todos los derechos reservados.</p>
            <p className="flex items-center gap-1 font-medium">
              <span>Fabricado con tecnología 3D aditiva & materiales bio</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
